import React from 'react'
import { useTheme } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import PaymentsIcon from '@mui/icons-material/Payments'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CategoryIcon from '@mui/icons-material/Category'
import StorefrontIcon from '@mui/icons-material/Storefront'
import Text from '../Text'
import KpiCard from '../shared/report/KpiCard'

// Consumo do gestor — o que o período custou, no mesmo recorte que o relatório
// já usa (Hoje / 7 dias / 30 dias / personalizado).
//
// O que aparece depende de quem paga a Meta nesta conexão, e isso vem decidido
// do backend:
//   - linha de crédito: gasto com a GLA, saldo e projeção. O custo na Meta não
//     existe aqui — é o preço de compra da GLA, embutido no preço contratado.
//   - taxa de serviço: a taxa que paga à GLA E o custo dele na Meta, que somados
//     dão o custo real do canal para ele.
//   - cartão próprio: só volume e o custo dele na Meta.
//
// Some inteiro quando não há dados (master antigo, provider não-oficial, erro):
// o relatório continua completo sem esta faixa.

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR')
// Preço por mensagem tem fração de centavo — R$ 0,0341 é informação, R$ 0,03 não é.
const fmtPorMsg = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })

const CAT_LABEL = {
  UTILITY: 'utilitário',
  MARKETING: 'marketing',
  AUTHENTICATION: 'autenticação',
  SERVICE: 'serviço',
  semCategoria: 'sem classificação'
}

// Texto do mix em uma linha: as duas categorias de maior volume bastam para o
// gestor reconhecer o próprio perfil de envio.
function resumoMix(mix) {
  const entradas = Object.entries(mix || {})
    .filter(([, n]) => Number(n) > 0)
    .sort((a, b) => b[1] - a[1])
  if (!entradas.length) return { valor: '—', hint: 'nenhum modelo enviado no período' }
  const total = entradas.reduce((s, [, n]) => s + Number(n), 0)
  const [principal] = entradas
  const pct = Math.round((principal[1] / total) * 100)
  const resto = entradas
    .slice(1, 3)
    .map(([cat, n]) => `${CAT_LABEL[cat] || cat} ${fmtNum(n)}`)
    .join(' · ')
  return {
    valor: `${pct}% ${CAT_LABEL[principal[0]] || principal[0]}`,
    hint: resto || `${fmtNum(principal[1])} mensagens`
  }
}

function textoProjecao(projecao) {
  if (!projecao || projecao.diasRestantes === null || projecao.diasRestantes === undefined) return 'sem projeção'
  const d = Number(projecao.diasRestantes)
  if (!Number.isFinite(d)) return 'sem projeção'
  if (d <= 0) return 'saldo esgotado'
  return `dura ~${fmtNum(d)} ${d === 1 ? 'dia' : 'dias'}`
}

export default function ConsumoCards({ dados, loading, periodLabel }) {
  const theme = useTheme()
  if (!dados && !loading) return null

  const d = dados || {}
  const temGasto = d.gasto !== null && d.gasto !== undefined
  const temCustoMeta = !!d.custoMeta
  const mix = resumoMix(d.mixPorCategoria)
  const saldo = d.saldo
  const saldoNegativo = saldo && Number(saldo.saldo) < 0
  const cor = {
    gasto: theme.palette.primary.main,
    saldo: saldoNegativo ? theme.palette.error.main : theme.palette.success.main,
    mix: '#8b5cf6',
    meta: '#0ea5e9'
  }

  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {temGasto && (
        <KpiCard
          compact
          label="Gasto no período"
          accent={cor.gasto}
          loading={loading}
          value={fmtBRL(d.gasto)}
          hint={`${fmtBRL(d.mediaDia)}/dia · ${fmtPorMsg(d.gastoPorMsg)} por mensagem`}
          icon={<PaymentsIcon style={{ fontSize: 22 }} />}
        />
      )}

      {saldo && (
        <KpiCard
          compact
          label="Saldo"
          accent={cor.saldo}
          loading={loading}
          value={fmtBRL(saldo.saldo)}
          hint={textoProjecao(d.projecao)}
          icon={<AccountBalanceWalletIcon style={{ fontSize: 22 }} />}
        />
      )}

      <KpiCard
        compact
        label={`Modelos · ${fmtNum(d.mensagens)} msgs`}
        accent={cor.mix}
        loading={loading}
        value={mix.valor}
        hint={mix.hint}
        icon={<CategoryIcon style={{ fontSize: 22 }} />}
      />

      {temCustoMeta && (
        <Tooltip
          title="Estimativa pela tabela de preços da Meta por categoria — é o que a Meta cobra de você direto, além do que paga aqui."
          arrow
        >
          <div>
            <KpiCard
              compact
              label="Custo na Meta"
              accent={cor.meta}
              loading={loading}
              value={fmtBRL(d.custoMeta.total)}
              hint={
                d.custoMeta.semClassificacao > 0
                  ? `${fmtPorMsg(d.custoMeta.porMsg)}/msg · ${fmtNum(d.custoMeta.semClassificacao)} sem classificação`
                  : `${fmtPorMsg(d.custoMeta.porMsg)} por mensagem · estimado`
              }
              icon={<StorefrontIcon style={{ fontSize: 22 }} />}
            />
          </div>
        </Tooltip>
      )}

      {/* Linha de crédito sem pacote contratado: não há gasto nem saldo a
          mostrar. Dizer o estado é melhor que estimar um valor que não será
          cobrado desse jeito. */}
      {!temGasto && !saldo && !temCustoMeta && (
        <div className="sm:col-span-2 xl:col-span-3 flex items-center">
          <Text className="text-xs opacity-60">
            Consumo de {periodLabel ? periodLabel.toLowerCase() : 'período'} sem cobrança associada — nenhum pacote contratado nesta conexão.
          </Text>
        </div>
      )}
    </div>
  )
}
